import React, { useEffect, useState } from 'react';
import { Button, TwIconButton, TwInput } from 'components/common';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTimes, FaFolderPlus, FaPen, FaSave, FaCheck, FaFolder } from 'react-icons/fa';
import { createAsset, updateAsset } from 'redux/drive/driveSlice';
import { FOLDER_COLORS, getFolderColorId, setFolderColor } from './folderColorStore';

const DriveFolderForm = ({ show, onClose, asset = null }) => {
  const dispatch = useDispatch();
  const { id: parentId } = useParams();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [colorId, setColorId] = useState('default');

  const isEdit = Boolean(asset);
  const isFolder = asset?.asset_type === 'folder';
  const HeaderIcon = isEdit ? FaPen : FaFolderPlus;
  const showColorPicker = isFolder; // tags are folder-only
  const currentColor = FOLDER_COLORS.find((c) => c.id === colorId) || FOLDER_COLORS[0];

  useEffect(() => {
    if (show) {
      setName(asset?.name || '');
      setError('');
      setSubmitting(false);
      setColorId(asset?.id ? getFolderColorId(asset.id) : 'default');
    }
  }, [show, asset]);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => e.key === 'Escape' && !submitting && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [show, submitting, onClose]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setError('Name is required'); return; }
    setSubmitting(true);
    try {
      let resolvedAssetId = asset?.id;
      if (isEdit) {
        await dispatch(updateAsset({ assetId: asset.id, data: { name: trimmed } })).unwrap();
      } else {
        const payload = parentId ? { name: trimmed, parent_id: parentId } : { name: trimmed };
        const result = await dispatch(createAsset(payload)).unwrap();
        resolvedAssetId = result?.data?.attributes?.id || result?.data?.id || resolvedAssetId;
      }
      // Persist folder color tag (folder-only, client-side)
      if (showColorPicker && resolvedAssetId) {
        setFolderColor(resolvedAssetId, colorId);
      }
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const modal = (
    <div
      onClick={submitting ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0,
        zIndex: 999999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        backgroundColor: 'rgba(15,23,42,0.45)',
        animation: 'driveFolderFadeIn 180ms ease',
      }}
    >
      <style>{`
        @keyframes driveFolderFadeIn { from { background-color: rgba(15,23,42,0); } to { background-color: rgba(15,23,42,0.45); } }
        @keyframes driveFolderPopIn { from { transform: translateY(8px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
      `}</style>

      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 440,
          backgroundColor: '#ffffff',
          borderRadius: 14,
          boxShadow: '0 20px 60px rgba(15,23,42,0.18), 0 4px 12px rgba(15,23,42,0.08)',
          overflow: 'hidden',
          animation: 'driveFolderPopIn 200ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 32, height: 32, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: '#eff6ff', color: '#2563eb',
            border: '1px solid #dbeafe', borderRadius: 8,
          }}>
            <HeaderIcon size={13} />
          </div>
          <h3 style={{
            flex: 1, margin: 0,
            fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.4,
          }}>
            {isEdit ? `Rename ${asset?.asset_type || 'item'}` : 'New folder'}
          </h3>
          <TwIconButton icon={FaTimes} size="md" variant="ghost" onClick={onClose} disabled={submitting} title="Close" />
        </div>

        {/* Body */}
        <div style={{ padding: '16px' }}>
          <TwInput
            label="Name"
            required
            value={name}
            onChange={(e) => { setName(e.target.value); setError(''); }}
            placeholder={isEdit ? 'Enter a new name' : 'Untitled folder'}
            error={error}
            disabled={submitting}
            size="md"
            autoFocus
          />

          {showColorPicker && (
            <div style={{ marginTop: 16 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 12, fontWeight: 700, color: '#334155',
                marginBottom: 8,
              }}>
                <span>Color tag</span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '1px 8px',
                  backgroundColor: currentColor.tint, color: currentColor.icon,
                  border: `1px solid ${currentColor.border}`,
                  borderRadius: 999, fontSize: 10, fontWeight: 700,
                }}>
                  <FaFolder size={9} /> {currentColor.label}
                </span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {FOLDER_COLORS.map((c) => {
                  const selected = c.id === colorId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setColorId(c.id)}
                      title={c.label}
                      aria-label={c.label}
                      disabled={submitting}
                      style={{
                        appearance: 'none', WebkitAppearance: 'none', boxShadow: 'none',
                        position: 'relative',
                        width: 30, height: 30, padding: 0, margin: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: c.tint,
                        color: c.icon,
                        border: `2px solid ${selected ? c.icon : c.border}`,
                        borderRadius: 8,
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        transition: 'border-color 120ms ease, transform 120ms ease',
                        transform: selected ? 'scale(1.06)' : 'scale(1)',
                      }}
                      onMouseEnter={(e) => { if (!submitting && !selected) e.currentTarget.style.transform = 'scale(1.06)'; }}
                      onMouseLeave={(e) => { if (!submitting && !selected) e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      <FaFolder size={12} />
                      {selected && (
                        <span style={{
                          position: 'absolute', top: -4, right: -4,
                          width: 14, height: 14,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: c.icon, color: '#ffffff',
                          border: '2px solid #ffffff',
                          borderRadius: 999,
                        }}>
                          <FaCheck size={6} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <p style={{
                margin: '10px 0 0', fontSize: 11, color: '#94a3b8', fontWeight: 500, lineHeight: 1.4,
              }}>
                Color tags are saved locally on this device.
              </p>
            </div>
          )}

          {!isEdit && parentId && (
            <p style={{
              margin: '10px 0 0', fontSize: 11, color: '#64748b', fontWeight: 500, lineHeight: 1.4,
            }}>
              The new folder will be created inside the current folder.
            </p>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="info" size="sm" type="submit" leftIcon={FaSave} loading={submitting}>
            {isEdit ? 'Save changes' : 'Create folder'}
          </Button>
        </div>
      </form>
    </div>
  );

  return createPortal(modal, document.body);
};

DriveFolderForm.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  asset: PropTypes.object,
};

export default DriveFolderForm;
